/**
 * Bundle thumbnail zoom (v2).
 *
 * Clicking a [data-bundle-zoom] trigger opens a fullscreen overlay gallery of
 * that product's images. Behaviour:
 *   - Lazy: image URLs are read from the trigger's data attributes (strings).
 *     No <img> is created / no src is set until the overlay is opened, so the
 *     large gallery images are NOT fetched on page load.
 *   - Swipe: the overlay is a CSS scroll-snap track. Touch-swipe moves between
 *     the product's images; prev/next arrow buttons do the same on desktop.
 *   - Deeper zoom: clicking the active image toggles a ~2.4x magnify centred on
 *     the click point (cursor zoom-in / zoom-out). While zoomed you can pan by
 *     moving the pointer. Clicking again toggles back out.
 *   - Close: backdrop click, the ✕ button, or Escape.
 *
 * Delegated click so it works for bundle cards re-rendered by JS.
 * Brand style: square corners (radius 0), white ✕ button.
 */
(function () {
  'use strict';

  var ZOOM_SCALE = 2.4;

  var overlay = null;
  var track = null; // scroll-snap container holding the slides
  var prevBtn = null;
  var nextBtn = null;
  var slides = []; // { slide, img } for the currently-open gallery
  var images = []; // image URLs for the open gallery
  var activeIndex = 0;

  function build() {
    overlay = document.createElement('div');
    overlay.setAttribute('data-bundle-zoom-overlay', '');
    overlay.style.cssText =
      'position:fixed;inset:0;z-index:1100;display:none;flex-direction:column;' +
      'align-items:stretch;justify-content:center;' +
      'background:rgba(0,0,0,0.8);-webkit-backdrop-filter:blur(4px);' +
      'backdrop-filter:blur(4px);cursor:zoom-out;';

    // Horizontal scroll-snap track. Each slide is a full-viewport flex cell.
    track = document.createElement('div');
    track.setAttribute('data-bundle-zoom-track', '');
    track.style.cssText =
      'flex:1 1 auto;display:flex;overflow-x:auto;overflow-y:hidden;' +
      'scroll-snap-type:x mandatory;scroll-behavior:smooth;' +
      '-webkit-overflow-scrolling:touch;scrollbar-width:none;';
    // Hide the scrollbar (WebKit).
    var styleTag = document.createElement('style');
    styleTag.textContent =
      '[data-bundle-zoom-track]::-webkit-scrollbar{display:none;}';
    document.head.appendChild(styleTag);

    // Close button.
    var close = document.createElement('button');
    close.type = 'button';
    close.setAttribute('aria-label', 'Fermer');
    close.setAttribute('data-bundle-zoom-close', '');
    close.innerHTML = '&times;';
    close.style.cssText =
      'position:absolute;top:16px;right:16px;width:40px;height:40px;border:0;' +
      'background:#fff;color:#101010;font-size:24px;line-height:1;cursor:pointer;' +
      'border-radius:0;display:flex;align-items:center;justify-content:center;' +
      'z-index:2;';
    close.addEventListener('click', function (e) {
      e.stopPropagation();
      hide();
    });

    prevBtn = makeArrow('prev', '‹');
    nextBtn = makeArrow('next', '›');
    prevBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      goTo(activeIndex - 1);
    });
    nextBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      goTo(activeIndex + 1);
    });

    overlay.appendChild(track);
    overlay.appendChild(close);
    overlay.appendChild(prevBtn);
    overlay.appendChild(nextBtn);

    // Backdrop click closes. The image and controls call stopPropagation, so
    // any click that bubbles up to the overlay is on empty space (overlay,
    // track, or a slide's padding) and should close.
    overlay.addEventListener('click', function () {
      hide();
    });

    // Keep activeIndex in sync when the user swipes/scrolls.
    var scrollRaf = null;
    track.addEventListener('scroll', function () {
      if (scrollRaf) return;
      scrollRaf = requestAnimationFrame(function () {
        scrollRaf = null;
        syncActiveFromScroll();
      });
    });

    document.addEventListener('keydown', function (e) {
      if (overlay.style.display === 'none') return;
      if (e.key === 'Escape') hide();
      else if (e.key === 'ArrowRight') goTo(activeIndex + 1);
      else if (e.key === 'ArrowLeft') goTo(activeIndex - 1);
    });

    document.body.appendChild(overlay);
  }

  function makeArrow(dir, glyph) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.setAttribute('aria-label', dir === 'prev' ? 'Image précédente' : 'Image suivante');
    btn.setAttribute('data-bundle-zoom-' + dir, '');
    btn.innerHTML = glyph;
    btn.style.cssText =
      'position:absolute;top:50%;transform:translateY(-50%);' +
      (dir === 'prev' ? 'left:16px;' : 'right:16px;') +
      'width:44px;height:44px;border:0;background:#fff;color:#101010;' +
      'font-size:28px;line-height:1;cursor:pointer;border-radius:0;' +
      'display:flex;align-items:center;justify-content:center;z-index:2;';
    return btn;
  }

  function makeSlide(index) {
    var slide = document.createElement('div');
    slide.style.cssText =
      'flex:0 0 100%;width:100%;height:100%;scroll-snap-align:center;' +
      'display:flex;align-items:center;justify-content:center;padding:24px;' +
      'box-sizing:border-box;overflow:hidden;';

    var img = document.createElement('img');
    img.alt = '';
    img.draggable = false;
    img.setAttribute('data-zoomed', 'false');
    img.style.cssText =
      'max-width:90vw;max-height:90vh;width:auto;height:auto;object-fit:contain;' +
      'border-radius:0;box-shadow:0 8px 40px rgba(0,0,0,0.4);cursor:zoom-in;' +
      'transition:transform 0.18s ease;transform-origin:center center;' +
      'transform:scale(1);will-change:transform;';

    img.addEventListener('click', function (e) {
      e.stopPropagation();
      toggleZoom(img, e);
    });
    img.addEventListener('mousemove', function (e) {
      if (img.getAttribute('data-zoomed') === 'true') panZoom(img, e);
    });

    slide.appendChild(img);
    return { slide: slide, img: img };
  }

  function toggleZoom(img, e) {
    var zoomed = img.getAttribute('data-zoomed') === 'true';
    if (zoomed) {
      img.setAttribute('data-zoomed', 'false');
      img.style.transformOrigin = 'center center';
      img.style.transform = 'scale(1)';
      img.style.cursor = 'zoom-in';
    } else {
      img.setAttribute('data-zoomed', 'true');
      setOrigin(img, e);
      img.style.transform = 'scale(' + ZOOM_SCALE + ')';
      img.style.cursor = 'zoom-out';
    }
  }

  function setOrigin(img, e) {
    var rect = img.getBoundingClientRect();
    var x = ((e.clientX - rect.left) / rect.width) * 100;
    var y = ((e.clientY - rect.top) / rect.height) * 100;
    x = Math.max(0, Math.min(100, x));
    y = Math.max(0, Math.min(100, y));
    img.style.transformOrigin = x + '% ' + y + '%';
  }

  function panZoom(img, e) {
    // Pan while zoomed: shift the transform-origin toward the pointer so the
    // user can explore the magnified image without a heavy pan library.
    setOrigin(img, e);
  }

  function resetZoom(entry) {
    if (!entry) return;
    entry.img.setAttribute('data-zoomed', 'false');
    entry.img.style.transformOrigin = 'center center';
    entry.img.style.transform = 'scale(1)';
    entry.img.style.cursor = 'zoom-in';
  }

  function goTo(index) {
    if (!slides.length) return;
    index = Math.max(0, Math.min(slides.length - 1, index));
    resetZoom(slides[activeIndex]);
    activeIndex = index;
    var target = slides[index].slide;
    track.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
    updateArrows();
  }

  function syncActiveFromScroll() {
    if (!slides.length) return;
    var center = track.scrollLeft + track.clientWidth / 2;
    var nearest = 0;
    var best = Infinity;
    for (var i = 0; i < slides.length; i++) {
      var s = slides[i].slide;
      var slideCenter = s.offsetLeft + s.offsetWidth / 2;
      var dist = Math.abs(slideCenter - center);
      if (dist < best) {
        best = dist;
        nearest = i;
      }
    }
    if (nearest !== activeIndex) {
      resetZoom(slides[activeIndex]);
      activeIndex = nearest;
      updateArrows();
    }
  }

  function updateArrows() {
    var multi = slides.length > 1;
    prevBtn.style.display = multi ? 'flex' : 'none';
    nextBtn.style.display = multi ? 'flex' : 'none';
    if (!multi) return;
    prevBtn.style.opacity = activeIndex <= 0 ? '0.4' : '1';
    prevBtn.style.pointerEvents = activeIndex <= 0 ? 'none' : 'auto';
    nextBtn.style.opacity = activeIndex >= slides.length - 1 ? '0.4' : '1';
    nextBtn.style.pointerEvents = activeIndex >= slides.length - 1 ? 'none' : 'auto';
  }

  function show(imageList, alt, startIndex) {
    if (!imageList || !imageList.length) return;
    if (!overlay) build();

    // Build slides and (now, on open) set the src so the large images are
    // fetched only when the user opens the zoom.
    track.innerHTML = '';
    slides = [];
    images = imageList;
    for (var i = 0; i < imageList.length; i++) {
      var entry = makeSlide(i);
      entry.img.src = imageList[i];
      entry.img.alt = alt || '';
      track.appendChild(entry.slide);
      slides.push(entry);
    }

    activeIndex = Math.max(0, Math.min(imageList.length - 1, startIndex || 0));
    overlay.style.display = 'flex';
    document.documentElement.style.overflow = 'hidden';
    updateArrows();

    // Jump (no smooth) to the start slide once layout is ready.
    requestAnimationFrame(function () {
      var target = slides[activeIndex].slide;
      track.scrollLeft = target.offsetLeft;
    });
  }

  function hide() {
    if (!overlay) return;
    overlay.style.display = 'none';
    track.innerHTML = '';
    slides = [];
    images = [];
    activeIndex = 0;
    document.documentElement.style.overflow = '';
  }

  function parseImages(trigger) {
    var list = trigger.getAttribute('data-bundle-zoom-images');
    if (list) {
      var urls = list.split('|').filter(function (u) {
        return u && u.trim();
      });
      if (urls.length) return urls;
    }
    // Backward-compatible fallback: single featured image.
    var single = trigger.getAttribute('data-bundle-zoom');
    return single ? [single] : [];
  }

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-bundle-zoom]');
    if (!trigger) return;
    e.preventDefault();
    e.stopPropagation();
    var imageList = parseImages(trigger);
    var featured = trigger.getAttribute('data-bundle-zoom');
    var startIndex = 0;
    if (featured) {
      var idx = imageList.indexOf(featured);
      if (idx >= 0) startIndex = idx;
    }
    show(imageList, trigger.getAttribute('data-bundle-zoom-alt'), startIndex);
  });
})();
